/**
 * Bundle builder ("Buy more, save more") for the featured collection sections.
 *
 * - Product cards toggle classes in and out of the cart.
 * - The fixed cart bar shows cart items, empty tier slots, the total and checkout.
 * - Discount tiers light up based on the number of paid classes in the cart.
 * - PostHog experiments switch how card prices are shown (productMode, productBadge).
 *
 * The cart (/cart.js) is the single source of truth: every change is queued, and once
 * the queue drains the cart is fetched again and the whole UI is re-synced from it.
 * Markup lives in sections/dev-featured-collection.liquid, sections/featured-collection.liquid,
 * snippets/dev-product-block.liquid and snippets/product-block.liquid.
 */
(function () {
  "use strict";

  const MOBILE_BREAKPOINT = 768;
  const REMOVE_FADE_MS = 600;
  const POSTHOG_WAIT_MS = 30000;
  const DEFAULT_TIERS = [30, 40, 50];
  const PRODUCT_MODES = ["regular", "discounted", "cart_discounted"];

  const state = {
    cart: null,
    tiers: DEFAULT_TIERS,
    isDev: false,
    isMobile: false,
    isExpanded: false,
    lastAdded: null,
    pending: 0,
    queue: Promise.resolve(),
    slider: { index: 0, step: 0, maxIndex: 0 },
  };

  const els = {};

  /* ---------- Helpers ---------- */

  function escapeHTML(value) {
    return String(value ?? "").replace(
      /[&<>"']/g,
      (char) =>
        ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[
          char
        ]
    );
  }

  function delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || "USD";

  // The new design drops ".00"; the legacy section always shows cents.
  function formatMoney(cents) {
    const trim = state.isDev && cents % 100 === 0;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      minimumFractionDigits: trim ? 0 : 2,
      maximumFractionDigits: trim ? 0 : 2,
    }).format(cents / 100);
  }

  function parseMoneyToCents(text) {
    const number = parseFloat(String(text || "").replace(/[^0-9.]/g, ""));
    return Number.isNaN(number) ? 0 : Math.round(number * 100);
  }

  function resizedImage(url, width) {
    if (!url) return "";
    try {
      const resized = new URL(url, window.location.origin);
      resized.searchParams.set("width", width);
      return resized.toString();
    } catch (error) {
      return url;
    }
  }

  function storageGet(key) {
    try {
      return window.localStorage.getItem(key);
    } catch (error) {
      return null;
    }
  }

  function storageSet(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch (error) {
      // Private mode or blocked storage: experiments fall back to the regular view.
    }
  }

  async function requestJSON(url, options) {
    const response = await fetch(url, {
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      ...options,
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.description || data.message || `${url} failed (${response.status})`);
    }
    return data;
  }

  /* ---------- Cart API ---------- */

  function routes() {
    const shopRoutes = window.routes || {};
    return {
      cart: `${shopRoutes.cart_url || "/cart"}.js`,
      add: `${shopRoutes.cart_add_url || "/cart/add"}.js`,
      change: `${shopRoutes.cart_change_url || "/cart/change"}.js`,
    };
  }

  function fetchCart() {
    return requestJSON(routes().cart, { method: "GET", headers: { Accept: "application/json" } });
  }

  function addVariants(variantIds) {
    if (variantIds.length === 0) return Promise.resolve({ items: [] });
    return requestJSON(routes().add, {
      method: "POST",
      body: JSON.stringify({ items: variantIds.map((id) => ({ id: Number(id), quantity: 1 })) }),
    });
  }

  function removeVariant(variantId) {
    return requestJSON(routes().change, {
      method: "POST",
      body: JSON.stringify({ id: String(variantId), quantity: 0 }),
    });
  }

  // Runs cart changes one at a time so responses can't arrive out of order,
  // then re-syncs the page from the real cart once nothing is left in flight.
  function enqueue(task) {
    state.pending++;
    state.queue = state.queue
      .then(task)
      .catch((error) => console.error("[bundle] Cart update failed:", error))
      .finally(() => {
        state.pending--;
        if (state.pending === 0) refreshCart();
      });
    return state.queue;
  }

  function refreshCart() {
    return fetchCart()
      .then((cart) => {
        state.cart = cart;
        render();
        document.dispatchEvent(new CustomEvent("cartUpdated", { detail: { cart } }));
      })
      .catch((error) => console.error("[bundle] Could not load cart:", error));
  }

  /* ---------- Cart derived values ---------- */

  function paidItemCount(cart) {
    return cart.items.filter((item) => item.original_price > 0).length;
  }

  // Tier index reached by the cart: 0 = none, 1 = first tier, ... capped at the last tier.
  function reachedTier(cart) {
    return Math.min(paidItemCount(cart), state.tiers.length);
  }

  function currentDiscount(cart) {
    const tier = reachedTier(cart);
    return tier > 0 ? state.tiers[tier - 1] : 0;
  }

  function nextDiscount(cart) {
    return state.tiers[Math.min(reachedTier(cart), state.tiers.length - 1)];
  }

  function readTiers() {
    const fromBlocks = Array.from(document.querySelectorAll(".sales-inner-2-item[data-discount]"))
      .map((item) => parseInt(item.dataset.discount, 10))
      .filter((value) => value > 0);
    if (fromBlocks.length) return fromBlocks;

    const fromList = Array.from(document.querySelectorAll(".sales-item .sales-value"))
      .map((item) => parseInt(item.textContent, 10))
      .filter((value) => value > 0);
    return fromList.length ? fromList : DEFAULT_TIERS;
  }

  /* ---------- Rendering ---------- */

  function render() {
    const cart = state.cart;
    if (!cart) return;

    document.querySelectorAll(".cart-count-badge").forEach((badge) => {
      badge.textContent = cart.item_count;
    });

    if (!els.cartBar) return;

    syncProductCards(cart);
    renderCartItems(cart);
    renderSummary(cart);
    renderTiers(cart);
    applyPriceMode(cart);
    requestAnimationFrame(updateSlider);
  }

  function syncProductCards(cart) {
    const productIds = new Set(cart.items.map((item) => String(item.product_id)));
    getProductCards().forEach((card) => setCardActive(card, productIds.has(card.dataset.productId)));
  }

  function setCardActive(card, isActive) {
    card.classList.toggle("active", isActive);
    const addButton = card.querySelector(".add-btn");
    if (addButton) addButton.classList.toggle("rotated", isActive);

    const toggle = card.querySelector("[data-bundle-toggle]");
    if (toggle) {
      const title = card.dataset.productTitle || "class";
      toggle.setAttribute("aria-pressed", String(isActive));
      toggle.setAttribute("aria-label", `${isActive ? "Remove" : "Add"} ${title} ${isActive ? "from" : "to"} cart`);
    }
  }

  function isItemVisible(index, count) {
    // The oldest class always stays visible; the rest collapse behind the "more classes" tile.
    const isOldest = index === count - 1;
    return isOldest || count <= 3 || state.isExpanded || (count > 5 && !state.isMobile);
  }

  function cartItemHTML(item, index, count) {
    const visible = isItemVisible(index, count);
    const title = escapeHTML(item.product_title || item.title);
    const image = escapeHTML(resizedImage(item.featured_image && item.featured_image.url, 360));
    const price = item.final_price > 0 ? formatMoney(item.final_price) : "Free";
    const animate = String(item.variant_id) === String(state.lastAdded) ? " animate" : "";
    const closeIcon = state.isDev
      ? `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" fill="none" aria-hidden="true">
          <circle cx="20" cy="20" r="20" fill="#F80590"/>
          <path d="M12.5134 27.4871L27.4875 12.513" stroke="white" stroke-width="3"/>
          <path d="M27.4872 27.4873L12.5132 12.5133" stroke="white" stroke-width="3"/>
        </svg>`
      : `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path fill-rule="evenodd" clip-rule="evenodd" d="M13.3333 3.74095L12.259 2.66667L7.99999 6.92572L3.74094 2.66667L2.66666 3.74095L6.9257 8L2.66666 12.259L3.74094 13.3333L7.99999 9.07429L12.259 13.3333L13.3333 12.259L9.07428 8L13.3333 3.74095Z" fill="white"/>
        </svg>`;

    return `
      <div class="cart-product${animate}" style="display: ${visible ? "flex" : "none"};">
        <button type="button" class="close-btn${state.isDev ? " dev-close-btn" : ""}" data-variant-id="${item.variant_id}" aria-label="Remove ${title} from cart">
          ${closeIcon}
        </button>
        <div class="product-image">
          <img src="${image}" alt="${title}" />
        </div>
        <div class="cart-price">${price}</div>
      </div>`;
  }

  function renderCartItems(cart) {
    const items = cart.items;
    const count = items.length;
    // Shopify lists the newest line first; the bar shows the oldest first.
    const displayed = items.map((item, index) => cartItemHTML(item, index, count)).reverse();

    const showTile = count > 3 && !state.isExpanded && (count < 6 || state.isMobile);
    const tileImage = count > 1 ? resizedImage(items[1].featured_image && items[1].featured_image.url, 360) : "";
    const tile = `
      <div class="more-classes" role="button" tabindex="0" style="display: ${showTile ? "flex" : "none"};" aria-label="Show all ${count} classes in cart">
        <div class="backdrop"></div>
        <img src="${escapeHTML(tileImage)}" alt="" />
        <p>${Math.max(count - 1, 0)} more classes</p>
      </div>`;

    const slots = [];
    if (!state.isDev && ((count >= 3 && count < 6 && !state.isMobile) || (count > 3 && state.isMobile))) {
      slots.push('<div class="cart-product-empty">Add a class</div>');
    }
    state.tiers.slice(count).forEach((discount) => {
      slots.push(`<div class="cart-product-empty">Add a class<br>to unlock ${discount}% savings</div>`);
    });

    els.products.innerHTML = tile + displayed.join("") + slots.join("");
    state.lastAdded = null;

    if (els.mobileCount) els.mobileCount.textContent = `${count} classes`;
  }

  function renderSummary(cart) {
    const discount = cart.original_total_price > 0
      ? Math.round(((cart.original_total_price - cart.total_price) / cart.original_total_price) * 100)
      : 0;

    if (els.total) els.total.textContent = formatMoney(cart.total_price);
    if (els.sales) {
      els.sales.innerHTML = `<span>${formatMoney(cart.original_total_price)}</span> ${discount}% off`;
    }
    if (els.checkout) els.checkout.textContent = `Checkout (${cart.items.length})`;
  }

  function renderTiers(cart) {
    const tier = reachedTier(cart);

    // Banner blocks: only the reached tier is highlighted.
    document.querySelectorAll(".sales-inner-2-item").forEach((item, index) => {
      item.classList.toggle("active", index === tier - 1);
    });

    // Legacy list: every reached tier is highlighted.
    document.querySelectorAll(".sales-item .sales-value").forEach((item, index) => {
      item.classList.toggle("active", index < tier);
    });
  }

  /* ---------- Price experiments ---------- */

  function getProductMode() {
    const mode = storageGet("productMode");
    return PRODUCT_MODES.includes(mode) ? mode : "regular";
  }

  function cardPriceCents(card) {
    const cents = parseInt(card.dataset.price, 10);
    return Number.isNaN(cents) ? parseMoneyToCents(card.dataset.originalPrice) : cents;
  }

  function applyPriceMode(cart) {
    const mode = getProductMode();
    const current = currentDiscount(cart);
    const next = nextDiscount(cart);

    getProductCards().forEach((card) => {
      const priceElement = card.querySelector(".product-price-current");
      const saleElement = card.querySelector(".product-price-sale");
      if (!priceElement) return;

      if (card.classList.contains("active")) {
        if (saleElement) saleElement.textContent = "";
        priceElement.innerHTML = state.isDev
          ? '<span class="product-price-current--added">Added to cart</span>'
          : "In Cart";
        return;
      }

      const price = cardPriceCents(card);
      let shownPrice = price;
      let struckPrice = card.dataset.originalComparePrice || "";

      if (mode === "discounted") {
        shownPrice = Math.round((price * (100 - next)) / 100);
        struckPrice = card.dataset.originalPrice || "";
      } else if (mode === "cart_discounted") {
        // What adding this class actually adds to the cart total once the next tier kicks in.
        const cartTotal = cart.original_total_price;
        shownPrice = Math.round(((cartTotal + price) * (100 - next) - cartTotal * (100 - current)) / 100);
        struckPrice = card.dataset.originalPrice || "";
      }

      priceElement.textContent = shownPrice > 0 ? formatMoney(shownPrice) : "Free";
      if (saleElement) saleElement.textContent = struckPrice;
    });
  }

  function applyProductBadge() {
    const enabled = storageGet("productBadge") === "enabled";
    document.querySelectorAll(".product-badge").forEach((badge) => {
      badge.style.display = enabled ? "flex" : "none";
    });
  }

  function saveProductModeFromUrl() {
    const mode = new URLSearchParams(window.location.search).get("product_mode") || "regular";
    if (PRODUCT_MODES.includes(mode)) storageSet("productMode", mode);
  }

  function initExperiments() {
    const startedAt = Date.now();

    (function waitForPostHog() {
      const posthog = window.posthog;
      if (posthog && typeof posthog.onFeatureFlags === "function") {
        posthog.onFeatureFlags(() => {
          storageSet("productMode", posthog.getFeatureFlag("bfcm-cart-discount") ? "cart_discounted" : "regular");
          storageSet("productBadge", posthog.getFeatureFlag("bfcm-callout") ? "enabled" : "disabled");
          if (state.cart) applyPriceMode(state.cart);
          applyProductBadge();
        });
      } else if (Date.now() - startedAt < POSTHOG_WAIT_MS) {
        setTimeout(waitForPostHog, 200);
      }
    })();
  }

  /* ---------- Cart bar slider & mobile overlay ---------- */

  function updateSlider() {
    if (!els.products || !els.wrapper) return;
    const visible = Array.from(els.products.querySelectorAll(".cart-product")).filter(
      (product) => product.style.display !== "none"
    );

    let step = 0;
    let perPage = 1;
    if (visible.length) {
      const first = visible[0].getBoundingClientRect();
      const gap = visible.length > 1 ? visible[1].getBoundingClientRect().left - first.left : 0;
      step = Math.round(gap > 0 ? gap : first.width);
      perPage = step > 0 ? Math.max(1, Math.floor(els.wrapper.clientWidth / step)) : visible.length;
    }

    state.slider.step = step;
    state.slider.maxIndex = Math.max(0, visible.length - perPage);
    state.slider.index = Math.min(state.slider.index, state.slider.maxIndex);

    const showArrows = !state.isMobile && visible.length > perPage;
    [els.leftArrow, els.rightArrow].forEach((arrow) => {
      if (arrow) arrow.style.display = showArrows ? "flex" : "none";
    });
    moveSlider();
  }

  function moveSlider() {
    els.products.style.transform = `translateX(-${state.slider.index * state.slider.step}px)`;
  }

  function slide(direction) {
    const nextIndex = state.slider.index + direction;
    if (nextIndex < 0 || nextIndex > state.slider.maxIndex) return;
    state.slider.index = nextIndex;
    moveSlider();
  }

  function expandCartItems() {
    state.isExpanded = true;
    if (state.isMobile) {
      els.wrapper.classList.add("active");
      els.products.style.flexWrap = "wrap";
      if (els.mobileInfo) els.mobileInfo.classList.add("active");
      document.body.style.overflow = "hidden";
    }
    render();
  }

  function collapseCartItems() {
    state.isExpanded = false;
    els.wrapper.classList.remove("active");
    els.products.style.flexWrap = "nowrap";
    if (els.mobileInfo) els.mobileInfo.classList.remove("active");
    document.body.style.overflow = "";
    render();
  }

  /* ---------- Product cards & tabs ---------- */

  function getProductCards() {
    return document.querySelectorAll(".product-card[data-variant-id]");
  }

  function toggleCard(card) {
    const variantId = card.dataset.variantId;
    const isAdding = !card.classList.contains("active");

    // Optimistic UI; the re-sync after the queue drains corrects it if the request fails.
    setCardActive(card, isAdding);
    if (state.cart) applyPriceMode(state.cart);

    if (isAdding) {
      state.isExpanded = false;
      enqueue(() => addVariants([variantId]).then(() => (state.lastAdded = variantId)));
    } else {
      enqueue(() => removeVariant(variantId));
    }
  }

  function removeFromCartBar(button) {
    const variantId = button.dataset.variantId;
    const item = button.closest(".cart-product");
    if (item) {
      item.style.transition = `opacity ${REMOVE_FADE_MS}ms`;
      item.style.opacity = "0";
    }
    const card = document.querySelector(`.product-card[data-variant-id="${CSS.escape(variantId)}"]`);
    if (card) setCardActive(card, false);

    enqueue(() => Promise.all([removeVariant(variantId), delay(REMOVE_FADE_MS)]));
  }

  function selectTag(tag) {
    const tagButtons = document.querySelectorAll(".tags-wrapper .tag");
    const target = Array.from(tagButtons).find((button) => button.dataset.tag === tag);
    if (!target) return false;

    tagButtons.forEach((button) => {
      const isActive = button === target;
      button.classList.toggle("active", isActive);
      button.setAttribute("aria-pressed", String(isActive));
    });

    let visibleCount = 0;
    getProductCards().forEach((card) => {
      const tags = (card.dataset.productTags || "").split(", ");
      const matches = tags.includes(tag);
      card.style.display = matches ? "flex" : "none";
      if (matches) visibleCount++;
    });

    const counter = document.querySelector(".classes-number");
    if (counter) counter.textContent = `${visibleCount} classes`;
    return true;
  }

  function selectDefaultTag() {
    const root = document.querySelector("[data-default-tag]");
    const defaultTag = root ? root.dataset.defaultTag : "";
    if (selectTag(defaultTag)) return;

    const firstTag = document.querySelector(".tags-wrapper .tag");
    if (firstTag) selectTag(firstTag.dataset.tag);
  }

  // Links like ?cls=123&cls=456 pre-fill the bundle, then the parameter is removed from the URL.
  function addClassesFromUrl() {
    const url = new URL(window.location.href);
    const variantIds = url.searchParams.getAll("cls").filter(Boolean);
    if (variantIds.length === 0) return false;

    url.searchParams.delete("cls");
    window.history.replaceState(window.history.state, "", url);

    enqueue(async () => {
      const cart = await fetchCart();
      const inCart = new Set(cart.items.map((item) => String(item.variant_id)));
      const toAdd = [...new Set(variantIds)].filter((id) => !inCart.has(String(id)));
      const result = await addVariants(toAdd);
      if (result.items && result.items.length) {
        selectTag(result.items[0].product_type);
        state.lastAdded = result.items[0].variant_id;
      }
    });
    return true;
  }

  /* ---------- Events ---------- */

  function bindEvents() {
    document.addEventListener("click", (event) => {
      const closeButton = event.target.closest(".cart-products .close-btn");
      if (closeButton) {
        removeFromCartBar(closeButton);
        return;
      }

      const toggle = event.target.closest(".product-card .add-btn, .product-card .add-btn-badge");
      if (toggle) {
        toggleCard(toggle.closest(".product-card"));
        return;
      }

      const tag = event.target.closest(".tags-wrapper .tag");
      if (tag) {
        selectTag(tag.dataset.tag);
        return;
      }

      if (event.target.closest(".cart-products .more-classes")) expandCartItems();
    });

    // Keyboard support for the controls that aren't native buttons.
    document.addEventListener("keydown", (event) => {
      if (event.key !== "Enter" && event.key !== " ") return;
      const toggle = event.target.closest("[data-bundle-toggle]");
      const moreClasses = event.target.closest(".cart-products .more-classes");
      if (!toggle && !moreClasses) return;
      event.preventDefault();
      if (toggle) toggleCard(toggle.closest(".product-card"));
      else expandCartItems();
    });

    if (els.leftArrow) els.leftArrow.addEventListener("click", () => slide(-1));
    if (els.rightArrow) els.rightArrow.addEventListener("click", () => slide(1));
    if (els.closeMobile) els.closeMobile.addEventListener("click", collapseCartItems);

    let resizeFrame = null;
    window.addEventListener("resize", () => {
      cancelAnimationFrame(resizeFrame);
      resizeFrame = requestAnimationFrame(() => {
        const wasMobile = state.isMobile;
        state.isMobile = window.innerWidth <= MOBILE_BREAKPOINT;
        if (wasMobile !== state.isMobile) render();
        else updateSlider();
      });
    });
  }

  /* ---------- Init ---------- */

  function init() {
    els.cartBar = document.querySelector(".cart .cart-products") ? document.querySelector(".cart") : null;

    if (!els.cartBar) {
      // Other pages only need the header cart count.
      if (document.querySelector(".cart-count-badge")) refreshCart();
      return;
    }

    els.products = els.cartBar.querySelector(".cart-products");
    els.wrapper = els.cartBar.querySelector(".cart-products-wrapper");
    els.leftArrow = els.cartBar.querySelector(".left-arrow");
    els.rightArrow = els.cartBar.querySelector(".right-arrow");
    els.mobileInfo = els.cartBar.querySelector(".mobile-info-wrapper");
    els.mobileCount = els.cartBar.querySelector(".mobile-info-wrapper p");
    els.closeMobile = els.cartBar.querySelector(".close-mobile-info");
    els.total = els.cartBar.querySelector(".cart-total span");
    els.sales = els.cartBar.querySelector(".cart-sales");
    els.checkout = els.cartBar.querySelector(".amount-btn");

    const hideButton = els.cartBar.querySelector(".hide-items-btn");
    if (hideButton) hideButton.style.display = "none";

    state.isDev = Boolean(document.querySelector(".dev-featured-collection"));
    state.isMobile = window.innerWidth <= MOBILE_BREAKPOINT;
    state.tiers = readTiers();

    bindEvents();
    selectDefaultTag();
    saveProductModeFromUrl();
    if (!addClassesFromUrl()) refreshCart();
    initExperiments();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
